import {
    createRentRollVersion,
    createRentRolls,
    deleteRentRolls,
    deleteRentRollVersion,
    fetchRentRollVersion,
    fetchRentRollVersions,
    processRentRollVersionEvent,
    replaceRentRollVersionData,
    updateRentRoll,
    updateRentRollVersion,
} from '@/api/mfh/rent-roll.api';
import { ToastType, enumToHuman, showToast } from '@/shared';
import { confirmDialog } from '@/shared/helpers';
import type {
    ICreateRentRollVersionParams,
    ICreateRentRollsParams,
    IFindRentRollVersionsParams,
    IRentRollDataSource,
    IUpdateRentRollParams,
    IUpdateRentRollVersionParams,
    RentRollVersionEvents,
    RentRollVersionExpandOpts,
} from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useDialog } from 'naive-ui';
import { computed, type MaybeRef, unref } from 'vue';

export const useRentRollVersion = (
    rentRollVersionId: MaybeRef<number | null | undefined>,
    expandOpts: RentRollVersionExpandOpts,
    enabled: MaybeRef<boolean> = true,
) => {
    const isEnabled = computed(() => Boolean(unref(rentRollVersionId)) && Boolean(unref(enabled)));

    return useQuery(
        ['rent-roll-version', rentRollVersionId, expandOpts],
        async () => {
            const currentRentRollVersionId = unref(rentRollVersionId);

            if (!currentRentRollVersionId) {
                throw new Error('Rent roll version id is required');
            }

            return fetchRentRollVersion(currentRentRollVersionId, expandOpts);
        },
        {
            initialData: null,
            enabled: isEnabled,
        },
    );
};

export const useRentRollVersions = (queryOpts: MaybeRef<IFindRentRollVersionsParams['query']>, expandOpts: RentRollVersionExpandOpts) => {
    return useQuery(['rent-roll-versions', queryOpts, expandOpts], async () => fetchRentRollVersions(unref(queryOpts), expandOpts), {
        initialData: [],
    });
};

export const useRentRollVersionMutations = () => {
    const queryClient = useQueryClient();
    const dialog = useDialog();

    const createRentRollVersionMutation = useMutation({
        mutationFn: async (params: ICreateRentRollVersionParams) => {
            return createRentRollVersion(params);
        },
        onSuccess() {
            queryClient.invalidateQueries(['rent-roll-version']);
            queryClient.invalidateQueries(['rent-roll-versions']);
        },
    });

    const updateRentRollVersionMutation = useMutation({
        mutationFn: async (params: IUpdateRentRollVersionParams) => {
            await updateRentRollVersion(params.rentRollVersionId, params.data);
        },
        onSuccess() {
            queryClient.invalidateQueries(['rent-roll-version']);
            queryClient.invalidateQueries(['rent-roll-versions']);
        },
    });

    const createRentRollsMutation = useMutation({
        mutationFn: async (params: ICreateRentRollsParams) => {
            return createRentRolls(params);
        },
        onSuccess() {
            queryClient.invalidateQueries(['rent-roll-version']);
            queryClient.invalidateQueries(['rent-roll-versions']);
        },
    });

    const updateRentRollMutation = useMutation({
        mutationFn: async (params: IUpdateRentRollParams) => {
            await updateRentRoll(params.rentRollId, params.data);
        },
        onSuccess() {
            queryClient.invalidateQueries(['rent-roll-version']);
            queryClient.invalidateQueries(['rent-roll-versions']);
        },
    });

    const deleteRentRollsMutation = useMutation({
        mutationFn: async (rentRollIds: number[]) => {
            await deleteRentRolls(rentRollIds);
        },
        onSuccess() {
            queryClient.invalidateQueries(['rent-roll-version']);
            queryClient.invalidateQueries(['rent-roll-versions']);
        },
    });

    const replaceRentRollVersionDataMutation = useMutation({
        mutationFn: async (params: { rentRollVersionId: number; source: IRentRollDataSource }) => {
            await replaceRentRollVersionData(params.rentRollVersionId, params.source);
        },
        onSuccess() {
            queryClient.invalidateQueries(['rent-roll-version']);
            queryClient.invalidateQueries(['rent-roll-versions']);
        },
    });

    const deleteRentRollVersionMutation = useMutation({
        mutationFn: async (rentRollVersionId: number) => {
            const { result: decision } = await confirmDialog(dialog, {
                title: 'Delete Rent Roll Version',
                content: 'Are you sure you want to delete this rent roll version? This cannot be undone.',
                okButton: { text: 'Delete', cssClass: 'is-danger' },
            });

            if (decision) {
                await deleteRentRollVersion(rentRollVersionId);
                return true;
            }
            return false;
        },
        onSuccess(deleted) {
            if (deleted) {
                queryClient.invalidateQueries(['rent-roll-version']);
                queryClient.invalidateQueries(['rent-roll-versions']);
            }
        },
    });

    const processRentRollEventMutation = useMutation({
        mutationFn: async (params: { rentRollVersionId: number; event: RentRollVersionEvents }) => {
            const { result: decision } = await confirmDialog(dialog, {
                title: 'Confirm state transition!',
                content: `Are you sure you want to trigger <b>${enumToHuman(params.event)}</b> for current Rent Roll Version?`,
                okButton: { text: 'Trigger!', cssClass: '' },
            });

            if (decision) {
                showToast(`Event: '${enumToHuman(params.event)}' triggered!`, ToastType.SUCCESS, 10000);

                try {
                    await processRentRollVersionEvent({
                        rentRollVersionId: params.rentRollVersionId,
                        event: params.event,
                    });
                    return true;
                } catch (_) {
                    showToast('Failed to trigger event!', ToastType.ERROR, 10000);
                    return false;
                }
            }

            return false;
        },
        onSuccess(isUpdated) {
            if (isUpdated) {
                queryClient.invalidateQueries(['rent-roll-version']);
                queryClient.invalidateQueries(['rent-roll-versions']);
            }
        },
    });

    return {
        createRentRollVersion: createRentRollVersionMutation,
        updateRentRollVersion: updateRentRollVersionMutation,
        createRentRolls: createRentRollsMutation,
        updateRentRoll: updateRentRollMutation,
        deleteRentRolls: deleteRentRollsMutation,
        processRentRollEvent: processRentRollEventMutation,
        replaceRentRollVersionData: replaceRentRollVersionDataMutation,
        deleteRentRollVersion: deleteRentRollVersionMutation,
    };
};
